import { useRef } from 'react';
import type { Background } from '../lib/useBackground';

/**
 * Background effects are opt-in and pull down a segmentation model on first
 * use, so the control says so rather than appearing to hang.
 */
export function BackgroundPicker({ background }: { background: Background }) {
  const fileRef = useRef<HTMLInputElement>(null);
  const { choice, setChoice, loading, error, supported } = background;

  if (!supported) return null;

  function pickImage(file: File | undefined) {
    if (!file) return;
    // A blob URL keeps the image in this tab. Nothing is uploaded — the
    // replacement happens on the local camera frames before they are encoded.
    if (choice.imageUrl) URL.revokeObjectURL(choice.imageUrl);
    setChoice({ mode: 'image', imageUrl: URL.createObjectURL(file) });
  }

  function clear() {
    if (choice.imageUrl) URL.revokeObjectURL(choice.imageUrl);
    setChoice({ mode: 'none' });
  }

  return (
    <div>
      <label htmlFor="background">Background</label>
      <div className="row" style={{ gap: '0.5rem' }}>
        <button
          type="button"
          className={choice.mode === 'none' ? 'primary' : 'ghost'}
          onClick={clear}
          aria-pressed={choice.mode === 'none'}
        >
          None
        </button>
        <button
          type="button"
          className={choice.mode === 'blur' ? 'primary' : 'ghost'}
          onClick={() => setChoice({ mode: 'blur' })}
          aria-pressed={choice.mode === 'blur'}
        >
          Blur
        </button>
        <button
          type="button"
          className={choice.mode === 'image' ? 'primary' : 'ghost'}
          onClick={() => fileRef.current?.click()}
          aria-pressed={choice.mode === 'image'}
        >
          {choice.mode === 'image' ? 'Change image' : 'Image…'}
        </button>
        <input
          ref={fileRef}
          id="background"
          type="file"
          accept="image/*"
          className="visually-hidden"
          onChange={(e) => pickImage(e.target.files?.[0])}
        />
      </div>

      {loading ? (
        <p className="faint" style={{ marginTop: '0.5rem' }}>
          Starting the effect — a 3 MB download the first time, then cached.
        </p>
      ) : null}

      {error ? (
        <p className="notice bad" role="alert" style={{ marginTop: '0.5rem' }}>
          {error}
        </p>
      ) : null}

      {choice.mode === 'image' && !loading && !error ? (
        <p className="faint" style={{ marginTop: '0.5rem' }}>
          Your image stays in this browser — the swap happens before anything is sent.
        </p>
      ) : null}
    </div>
  );
}
